import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Label,
  Line,
  ReferenceLine,
} from "recharts";

import BarLabelWithNullZeros from "../RechartHelpers/BarLabelWithNullZeros";
import LineDotWithNullZero from "../RechartHelpers/LineDotWithNullZero";
import { FONT_SIZE_EXTRA_SMALL } from "reports/constants/constants";
import {
  MAX_BAR_SIZE,
  renderCustomBar,
  renderCustomLineLabel,
} from "./BarCharts/GenericComposedChart";
import {
  CHART_HEIGHT,
  RECHARTS_FULL_WIDTH,
} from "../BespokeReports/bespokeReportUtils";
import { customDateTick } from "../RechartHelpers/rechartTickUtils";
import { GenericComposedChartStyles } from "./BarCharts/barChartStyles";
import { useMediaQuery } from "@mui/material";

interface AxisConfig {
  id: string;
  label: string;
  orientation: "left" | "right";
  scaleType: "percentage" | "number";
  domain: "auto" | number[];
}

export default function GenericBiAxialComposedChart({
  title,
  data,
  xKey = "date",
  xLabel,
  axes = [],
  bars,
  lines = [],
  width = RECHARTS_FULL_WIDTH,
  height = CHART_HEIGHT,
  useGrid = true,
  legend,
  targetLines = [],
  line1References = [],
  line2References = [],
}: {
  title?: string;
  data: any[];
  xKey?: string;
  xLabel: string;
  axes?: AxisConfig[];
  bars?: any[];
  lines?: any[];
  width?: number;
  height?: number;
  useGrid?: boolean;
  legend: any;
  targetLines?: any[];
  line1References?: any[];
  line2References?: any[];
}) {
  const isPrint = useMediaQuery("print");

  return (
    <GenericComposedChartStyles hideYAxis={false}>
      <div className="chartBody">
        <p className="chartTitle">{title}</p>
        <ComposedChart
          width={width}
          height={height}
          data={data}
          margin={{
            top: 30,
            right: 50,
            left: 30,
            bottom: 30,
          }}
          stackOffset="sign"
        >
          {axes.map(axis => (
            <YAxis
              key={axis.id}
              yAxisId={axis.id}
              orientation={axis.orientation}
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: FONT_SIZE_EXTRA_SMALL }}
              domain={axis.domain === "auto" ? ["auto", "auto"] : axis.domain}
              scale={axis.scaleType === "percentage" ? "linear" : "auto"}
            >
              <Label
                value={axis.label}
                position={axis.orientation}
                angle={axis.orientation === "left" ? -90 : -270}
                style={{ textAnchor: "middle" }}
              />
            </YAxis>
          ))}
          {useGrid ? (
            <CartesianGrid vertical={false} horizontal={true} />
          ) : null}
          <XAxis key={xKey} dataKey={xKey} tick={customDateTick()} interval={0}>
            <Label
              key={xLabel}
              value={xLabel}
              offset={-15}
              position="insideBottom"
            />
          </XAxis>
          {legend}
          <Tooltip />
          {bars?.map(({ key, name, stackId, fill, yAxisId, label }) => {
            return (
              <Bar
                key={key}
                dataKey={key}
                name={name}
                stackId={stackId}
                fill={fill}
                yAxisId={yAxisId}
                isAnimationActive={false}
                maxBarSize={MAX_BAR_SIZE}
                shape={renderCustomBar(height)}
                overflow="visible"
              >
                {label ? (
                  <LabelList
                    key={key}
                    content={
                      label.content
                        ? label.content
                        : (BarLabelWithNullZeros as any)
                    }
                    position="top"
                  />
                ) : null}
              </Bar>
            );
          })}
          {targetLines?.map(
            ({ name, stroke, key, hideLabel, fillDot, yAxisId }) => {
              return (
                <Line
                  key={key + Math.random()}
                  type="linear"
                  yAxisId={yAxisId}
                  dot={fillDot ? { fill: stroke } : true}
                  name={name}
                  {...{
                    label: hideLabel ? "" : renderCustomLineLabel(isPrint),
                    strokeDashArray: "2 2",
                  }} // recharts 2.0.8-9 typescript bug
                  dataKey={key}
                  stroke={stroke}
                  isAnimationActive={false}
                />
              );
            }
          )}
          {lines.map(({ key, name, yAxisId, fill, textColor, strokeWidth }) => {
            return (
              <Line
                {...{ name, yAxisId, strokeWidth }}
                key={key}
                dataKey={key}
                dot={LineDotWithNullZero(fill, textColor) as any}
                stroke={fill}
                isAnimationActive={false}
                connectNulls={false}
              />
            );
          })}
          {line1References.map(({ yValue, xValue, label }) => (
            <ReferenceLine
              key={label}
              y={yValue ? yValue : null}
              x={xValue ? xValue : null}
              yAxisId="left"
              ifOverflow="extendDomain"
            >
              <Label value={label} position={"insideRight"} />
            </ReferenceLine>
          ))}
          {line2References.map(({ yValue, xValue, label }) => (
            <ReferenceLine
              key={label}
              y={yValue ? yValue : null}
              x={xValue ? xValue : null}
              yAxisId="right"
              ifOverflow="extendDomain"
            >
              <Label value={label} position={"insideRight"} />
            </ReferenceLine>
          ))}
        </ComposedChart>
      </div>
    </GenericComposedChartStyles>
  );
}
